import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Typography } from '@mui/material';
import {
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where
} from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';

import { companiesRef } from '../../../api/firebase/firestore/company';
import { mediasRef } from '../../../api/firebase/firestore/media';
import { usersRef } from '../../../api/firebase/firestore/user';
import {
  black,
  blackText,
  blue,
  blueGrayText,
  primaryButton
} from '../../../constants/Colors';
import {
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper
} from '../../../constants/Location';
import { screenType, status } from '../../../constants/enum';
import { AuthContext } from '../../../contexts/auth';
import { throwError } from '../../../modules/error';
import CustomLoading from '../../uiParts/CustomLoading';
import MediaCard from '../../uiParts/MediaCard';
import NothingGivenList from '../../uiParts/NothingGivenList';
import Sidebar from '../../uiParts/Sidebar';

import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

const styles = {
  aCard: {
    '@media screen and (max-width:470px)': {
      mt: '12px'
    },
    mt: '24px'
  },
  avatar: {
    height: '110px',
    marginTop: '-45px',
    width: '110px'
  },
  avatarMl: {
    marginLeft: '24px'
  },
  button: {
    '&:hover': {
      '.MuiSvgIcon-root': {
        opacity: '70%'
      },
      '.MuiTypography-root': {
        opacity: '70%'
      },
      backgroundColor: '#FFF'
    },
    '@media screen and (max-width:960px)': {
      height: '56px'
    },
    backgroundColor: '#FFF',
    boxSizing: 'border-box',
    height: '80px',
    padding: '24px'
  },
  buttonFont: {
    fontSize: '13px'
  },
  buttonLocation: {
    height: '0',
    position: 'sticky',
    textAlign: 'right',
    top: '15px'
  },
  buttonText: {
    '.MuiTypography-root': {
      '@media screen and (max-width:960px)': {
        fontSize: '18px'
      },
      fontSize: '22px'
    },
    color: blackText
  },
  card: {
    border: '1px solid #DBDBE0',
    borderRadius: '4px',
    boxShadow: 0,
    margin: '24px'
  },
  category: {
    color: blueGrayText,
    fontSize: '14px'
  },
  categoryTitle: {
    color: black,
    fontSize: '20px',
    fontWeight: 700
  },
  companyName: {
    '@media screen and (max-width:480px)': { fontSize: '16px' },
    color: black,
    fontSize: '20px',
    fontWeight: 700
  },
  contactButton: {
    '@media screen and (max-width:960px)': {
      height: '30px',
      padding: '4px 10px'
    },
    padding: '6px 16px'
  },
  contactButtonText: {
    color: black,
    fontSize: '14px'
  },
  container: {
    alignItems: 'center',
    display: 'flex'
  },
  content: {
    textAlign: 'left'
  },
  description: {
    '@media screen and (max-width:480px)': { fontSize: '12px' },
    color: black,
    fontSize: '14px',
    marginTop: '30px'
  },
  divider: {
    backgroundColor: '#DBDBE0',
    color: black,
    marginBottom: '8px',
    marginTop: '8px'
  },
  fontMd: {
    color: black,
    fontSize: '14px'
  },
  fontSm: {
    fontSize: '12px'
  },
  guideButton: {
    '@media screen and (max-width:960px)': {
      left: 0,
      width: '327px'
    },
    backgroundColor: blue,
    bottom: '56px',
    height: '42px',
    left: '256px',
    margin: 'auto',
    maxWidth: '344px',
    position: 'absolute',
    right: 0
  },
  guideButtonText: {
    fontSize: '15px',
    marginRight: '8px'
  },
  heading: {
    fontWeight: 'bold'
  },
  icon: {
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px'
    },
    color: blackText,
    height: '32px',
    width: '32px'
  },
  item: {
    padding: '8px'
  },
  list: {
    '@media screen and (max-width:470px)': {
      margin: '0 16px'
    },
    margin: '0 24px'
  },
  media: {
    height: '133px',
    margin: '32px 40px',
    width: '245px'
  },
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper,
  subheading: {
    lineHeight: 1.8
  },
  text: {
    color: black,
    fontSize: '14px'
  },
  textMt: {
    marginTop: '16px'
  }
};

const PublisherMypageMedia: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = t('title.publisher.media');
  });

  interface Media {
    mediaId: string;
    companyId: number;
    created: Date;
    updated: Date;
    status: number;
    assigneeId: number;
    name: string;
    category: [];
    overview: string;
    funnel: number[];
    mediaType: number;
    target: number[];
    seasonalMenuMaterialText: string;
    value: string;
    industries: Array<{
      industriesNum: number;
      industriesOther: string;
      percentage: number;
    }>;
    continuousFactors: string[];
    image: string;
    demographicsGeneration:
      | {
          f10: number;
          f20: number;
          f30: number;
          f40: number;
          f50: number;
          f60: number;
          f70: number;
          m10: number;
          m20: number;
          m30: number;
          m40: number;
          m50: number;
          m60: number;
          m70: number;
        }
      | undefined;
    demographicsGender:
      | {
          male: number;
          female: number;
        }
      | undefined;
  }

  const [hasMore, setHasMore] = useState(true);
  const [medias, setMedias] = useState<Media[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showList, setShowList] = useState<boolean>(false);

  const appendQuestions = (
    snapshot: Array<QueryDocumentSnapshot<DocumentData>>
  ): void => {
    const gotMedias: Media[] = snapshot.map((document) => {
      const media = document.data() as Media;
      return media;
    });
    setMedias(medias.concat(gotMedias));
  };

  const { currentUser } = useContext(AuthContext);
  const [companyStatus, setCompanyStatus] = useState<number>(0);

  useEffect(() => {
    void (async () => {
      const userRef = doc(usersRef, currentUser?.uid);
      const userSnap = await getDoc(userRef);
      const ownCompanyId = userSnap.data()?.companyId as string;
      // 会社自体のステータスを確認
      const companyRef = doc(companiesRef, ownCompanyId);
      const companySnap = await getDoc(companyRef);
      if (companySnap.exists()) {
        setCompanyStatus(companySnap.data().status as number);
      }
    })();
  }, [currentUser?.uid]);

  const loadMore = async (): Promise<void> => {
    const userRef = doc(usersRef, currentUser?.uid);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const ownCompanyId = userSnap.data()?.companyId as string;

      const lastQuestion = medias[medias.length - 1];

      const data = !medias.length
        ? query(
            mediasRef,
            where('companyId', '==', ownCompanyId),
            where('status', 'in', [
              status.approved,
              status.notChecked,
              status.fix
            ]),
            orderBy('created', 'desc'),
            limit(50)
          )
        : query(
            mediasRef,
            where('companyId', '==', ownCompanyId),
            where('status', 'in', [
              status.approved,
              status.notChecked,
              status.fix
            ]),
            orderBy('created', 'desc'),
            startAfter(lastQuestion.created),
            limit(50)
          );

      const { docs, empty } = await getDocs(data);

      if (empty) {
        setHasMore(false);
        return;
      }
      appendQuestions(docs);
    }
  };

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        await loadMore();
      } catch (e) {
        throwError(e);
      } finally {
        setShowList(true);
        setLoading(false);
      }
    })();
  }, []);

  return (
    <>
      <CustomLoading loading={loading} />
      <Typography component="div" sx={styles.mypageWrapper}>
        <Sidebar screenType={screenType.publisher} />
        <Typography
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            position: 'static',
            width: '100%'
          }}
        >
          <Typography sx={styles.mypageHeader} component="div">
            <Typography sx={styles.mypageHeaderTitle}>
              {t('media.mediaIndex')}
            </Typography>
            <Button
              variant="contained"
              sx={primaryButton}
              onClick={() => {
                navigate('/publisher/mypage/media/create');
              }}
            >
              ＋ {t('media.register')}
            </Button>
          </Typography>
          {showList && (
            <Typography component="div" sx={{ mb: '40px' }}>
              {medias.length !== 0 ? (
                <Typography sx={styles.list} component="div">
                  <InfiniteScroll
                    loadMore={() => {
                      void (async () => {
                        await loadMore();
                      })();
                    }}
                    hasMore={hasMore}
                    loader={<div className="loader" key={0}></div>}
                  >
                    {medias.map((value, i) => (
                      <Typography key={i} sx={styles.aCard}>
                        <MediaCard
                          screen={screenType.publisher}
                          category={value?.category}
                          image={value?.image}
                          title={value?.name}
                          summaryText={value?.overview ?? ''}
                          funnel={value?.funnel}
                          target={value?.target}
                          seasonalMenuMaterialText={
                            value?.seasonalMenuMaterialText
                          }
                          mediaType={value?.mediaType}
                          industries={value?.industries ?? ''}
                          demographicsGeneration={value?.demographicsGeneration}
                          demographicsGender={value?.demographicsGender}
                          mediaId={value?.mediaId}
                          mediaStatus={
                            companyStatus === 1 ? value?.status : companyStatus
                          }
                        />
                      </Typography>
                    ))}
                  </InfiniteScroll>
                </Typography>
              ) : (
                <NothingGivenList
                  title={t('media.noMedia')}
                  detail={t('media.noMediaDesc')}
                />
              )}
            </Typography>
          )}
        </Typography>
      </Typography>
    </>
  );
};

export default PublisherMypageMedia;
